import { useGuestPageContainerBySlugQuery } from './Custom.graphql.generated';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import type { MinimalPage } from '@shared/hooks/useDefaultPage';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { EventPageType } from '@graphql/generated-base';
import { useIsActiveTarget, useSignalPageLoaded } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { isInIframe } from '@shared/utils/isInIframe';
import { resolveCustomDataRequest, toCustomDataVariables } from './utils/customDataRequest';
import { useIsStaysRedesignActive } from './components/Honeymoon/useIsStaysRedesignActive';

const WELCOME_PAGE_SLUG = 'welcome';

// Prefetches GuestPageContainerBySlug so the Custom widget reads from cache.
// SSR fires for migrated-welcome (LCP) or honeymoon-redesign events (PRNT-4234).
export const useFetchCustomPageData = (eventHandle: string, skip?: boolean, pages?: ReadonlyArray<MinimalPage>) => {
  const { isAdmin } = useEventUserRole();
  const isPreviewing = isInIframe();
  const { currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug } = useCurrentPageSlug(eventHandle);
  const { defaultPage } = useDefaultPage(pages);

  const isDefaultCustomRoot = !currentPageSlug && defaultPage?.type === EventPageType.custom;
  const isMigratedWelcomeRoot = isDefaultCustomRoot && defaultPage.pageSlug === WELCOME_PAGE_SLUG;
  const effectivePageSlug = currentPageSlug ?? (isDefaultCustomRoot ? defaultPage.pageSlug : '');
  const isActiveTarget = useIsActiveTarget(effectivePageSlug ? [effectivePageSlug] : []);
  // registrylinks → registry; no-op for every other migration. A userCustom squatter is
  // fetched by its literal slug (CORE-6456) so it doesn't resolve the system container.
  // PRNT-4817: mirrors useCustomData's variables so the Custom widget reads this prefetch from cache.
  // Callers pass no `pages`, so a contested `/page/<slug>` (the only case that sends `pageKind`) still
  // fetches twice. See useCustomData for why only that URL may name a `pageKind`.
  const { containerSlug, routedPageKind } = resolveCustomDataRequest(effectivePageSlug, pages, isUrlBasedCustomPageSlug);

  // Defense-in-depth: also gated at the call site in usePrefetchSsrQueries.
  const isStaysRedesignActive = useIsStaysRedesignActive();

  const { data, loading } = useGuestPageContainerBySlugQuery({
    variables: toCustomDataVariables(eventHandle, containerSlug, isPreviewing && isAdmin, routedPageKind),
    // Active-target route fires unbatched for fastest TTFB.
    batchMode: isActiveTarget ? 'off' : 'fast',
    fetchPolicy: 'cache-first',
    // SSR fires for migrated-welcome (LCP) or honeymoon-redesign events.
    ssr: isMigratedWelcomeRoot || isStaysRedesignActive,
    // Tolerate partial BE errors so SSR doesn't 500.
    errorPolicy: 'all',
    skip: skip || (!isCustomPageSlug && !isDefaultCustomRoot) || !containerSlug,
  });

  // Null pageContainerBySlug (e.g. invalid slug) must not trigger chrome reveal.
  const hasLoadedCustomPageData = !loading && !!data?.pageContainerBySlug;
  useSignalPageLoaded(isActiveTarget, hasLoadedCustomPageData);

  return { data, loading };
};
